/**
 * Global error boundary — wraps the entire app so a render crash anywhere
 * (React component throwing during render) shows a recoverable error page
 * instead of a WHITE SCREEN.
 *
 * Why a class component: React error boundaries MUST be class components.
 * Functional equivalents don't exist as of React 18.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for debugging — still visible in DevTools Console.
    console.error("[ErrorBoundary] caught:", error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const err = this.state.error;
    const msg =
      err?.message ||
      (typeof err === "string" ? err : "Unknown error");
    const stack = err?.stack || "";

    return (
      <div
        data-testid="error-boundary-root"
        className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6"
      >
        <div className="bg-white rounded-2xl border border-red-200 p-8 max-w-xl w-full shadow-lg">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-serif text-[#2A3324] mb-1">
                Something went wrong on this page
              </h1>
              <p className="text-sm font-sans text-[#2A3324]/60">
                The action you just took caused the page to crash. Your data
                is safe — the server has already saved whatever went through.
                You can safely reload or go back.
              </p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
            <p className="text-xs font-sans font-medium text-red-900 mb-1">
              Error details
            </p>
            <code className="text-[11px] font-mono text-red-800 break-all">
              {msg}
            </code>
          </div>

          {process.env.NODE_ENV !== "production" && stack && (
            <details className="mb-5">
              <summary className="text-xs font-sans text-[#2A3324]/60 cursor-pointer hover:text-[#2A3324]">
                Stack trace (dev only)
              </summary>
              <pre className="mt-2 text-[10px] font-mono text-[#2A3324]/70 bg-[#F7F5F0] p-3 rounded overflow-auto max-h-48">
                {stack}
              </pre>
            </details>
          )}

          <div className="flex gap-3">
            <button
              data-testid="error-boundary-reload"
              onClick={this.reload}
              className="bg-[#5C715E] text-[#F7F5F0] rounded-xl px-5 py-2.5 text-sm font-sans font-medium hover:bg-[#4A5A4B] transition-all"
            >
              Reload page
            </button>
            <button
              data-testid="error-boundary-home"
              onClick={() => {
                window.location.href = "/admin";
              }}
              className="bg-white border border-[#2A3324]/10 text-[#2A3324]/70 rounded-xl px-5 py-2.5 text-sm font-sans hover:bg-[#F7F5F0]"
            >
              Back to dashboard
            </button>
            <button
              data-testid="error-boundary-dismiss"
              onClick={this.reset}
              className="text-sm font-sans text-[#2A3324]/50 hover:text-[#2A3324] px-3 py-2.5"
            >
              Try to continue anyway
            </button>
          </div>
        </div>
      </div>
    );
  }
}
